import type {
  PaymentInstrument,
  PaymentModuleId,
} from '@foundation/apis/payments';
import Clarity from '@microsoft/clarity';

import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import { Startupable } from '@/services/base/Startupable';
import { registerStartupable } from '@/services/base/StartupHelper';
import Logger, { LoggerOrigin } from '@/services/Logger';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { createId } from '@/utils/ids';

import { NavigationSubscription } from './NavigationSubscription';
import PaymentStore from '../payments/PaymentStore';

export enum InternalPage {
  PaymentMethods = 'PaymentMethods',
  StatusProcessing = 'StatusProcessing',
  StatusSuccess = 'StatusSuccess',
  StatusError = 'StatusError',
  StoredInstruments = 'StoredInstruments',
  SessionExpired = 'SessionExpired',
  BankTransfer = 'BankTransfer',
  Finvoice = 'Finvoice',
  FinvoiceStatusSuccess = 'FinvoiceStatusSuccess',
  PaymentOnDelivery = 'PaymentOnDelivery',
  PaymentOnDeliveryStatusSuccess = 'PaymentOnDeliveryStatusSuccess',
  PaymentInSubsidiaryStatusSuccess = 'PaymentInSubsidiaryStatusSuccess',
  SepaMandateConsent = 'SepaMandateConsent',
  SepaMandateActivation = 'SepaMandateActivation',
  PayByLink = 'PayByLink',
  LsvMandateSummary = 'LsvMandateSummary',
  PostFinanceMandateSummary = 'PostFinanceMandateSummary',
  PayByInvoiceStatusSuccess = 'PayByInvoiceStatusSuccess',
  BankTransferStatusSuccess = 'BankTransferStatusSuccess',
}

export type Page = { readonly page: InternalPage };

type CallbackFn = (currentPage: Page) => void;

class NavigationService implements Startupable {
  public readonly name = 'NavigationService';

  private observers: Record<string, CallbackFn> = {};
  private currentPage: Page = { page: InternalPage.StoredInstruments };

  constructor() {
    registerStartupable(this, [PaymentStore]);
  }

  public startup(): Promise<void> {
    this.observers = {};
    this.updateCurrentPageAndTrackPageView(this.computeInitialPage());
    return Promise.resolve();
  }

  public subscribe(callback: CallbackFn, customId?: string) {
    const id = customId ?? createId();

    if (this.observers[id]) {
      Logger.warn(
        LoggerOrigin.NavigationService,
        `The id "${id}" is already taken! Beware the callback will be overwritten!`,
      );
    }

    this.observers[id] = callback;
    callback(this.currentPage);
    return new NavigationSubscription(id);
  }

  public unsubscribe(id: string) {
    delete this.observers[id];
  }

  public getInternalPage() {
    return this.currentPage;
  }

  public navigateTo(page: Page) {
    if (this.currentPage !== page) {
      this.updateCurrentPageAndTrackPageView(page);

      Logger.log(
        LoggerOrigin.NavigationService,
        `Updated currentPage to "${this.currentPage}"`,
      );
      this.notifyObservers();
      Clarity.setTag('navigation', page.page);
    }
  }

  private updateCurrentPageAndTrackPageView(page: Page): void {
    this.currentPage = page;
    TelemetryService.trackPageView({ name: this.currentPage.page });
  }

  private notifyObservers() {
    Object.keys(this.observers).forEach((id) => {
      try {
        this.observers[id](this.currentPage);
      } catch (err) {
        Logger.error(
          LoggerOrigin.NavigationService,
          `Failed to notify "${id}". Don't forget to unsubscribe! This time I will do it for you.`,
          { cause: err },
        );
        this.unsubscribe(id);
      }
    });
  }

  private computeInitialPage(): Page {
    // For moto flow start the application in the list of methods
    if (
      this.isMotoFlow(PaymentStore.flow) ||
      this.isEmptyListOfInstruments(PaymentStore.instruments) ||
      this.hasNewMethodPreSelected(
        PaymentStore.rawPreSelectedPaymentMethod,
        PaymentStore.preSelectedInstrument,
      )
    ) {
      return { page: InternalPage.PaymentMethods };
    }

    return { page: InternalPage.StoredInstruments };
  }

  private isMotoFlow(flow: PaymentIntentFlow) {
    return flow === PaymentIntentFlow.Moto;
  }

  private isEmptyListOfInstruments(
    instruments: ReadonlyArray<PaymentInstrument>,
  ) {
    return instruments.length === 0;
  }

  private hasNewMethodPreSelected(
    rawPreSelection: PaymentModuleId | undefined,
    preSelectedInstrument: PaymentInstrument | undefined,
  ) {
    return rawPreSelection !== undefined && preSelectedInstrument === undefined;
  }
}

export default new NavigationService();
